import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "../assistant.css";

const markdownLinkRenderer = {
  a: ({ node, children, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer">{children}</a>
  ),
};
import {
  FiArrowLeft,
  FiChevronDown,
  FiCheckCircle,
  FiSend,
  FiCpu,
  FiUser,
  FiSearch,
  FiArrowRight
} from "react-icons/fi";
import { FaBroom } from "react-icons/fa";

function ChatAssistantWindow({ messages, onSubmitQuery, onActionClick, onClear, initialInputValue = "", onBackHome }) {
  const [inputValue, setInputValue] = useState(initialInputValue);
  const chatScrollRef = useRef(null);
  const inputRef = useRef(null);

  const handleSubmit = (event) => {
    event.preventDefault();
    const trimmed = inputValue.trim();

    if (!trimmed) {
      return;
    }

    onSubmitQuery(trimmed, { fromChat: true });
    setInputValue("");
  };

  const handleClearChat = () => {
    setInputValue("");
    onClear();
  };

  const handleInputKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey && !event.altKey && !event.ctrlKey) {
      event.preventDefault();
      const trimmed = inputValue.trim();
      if (!trimmed) {
        return;
      }
      onSubmitQuery(trimmed, { fromChat: true });
      setInputValue("");
    }
  };

  useEffect(() => {
    if (!chatScrollRef.current) {
      return;
    }

    chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    setInputValue(initialInputValue || "");
  }, [initialInputValue]);

  useEffect(() => {
    if (!inputRef.current) {
      return;
    }

    const style = window.getComputedStyle(inputRef.current);
    const lineHeight = Number.parseFloat(style.lineHeight) || 20;
    const baseHeight = 40;
    const maxLines = 5;
    const maxHeight = baseHeight + lineHeight * (maxLines - 1);

    inputRef.current.style.height = "auto";
    const contentHeight = inputRef.current.scrollHeight;
    const nextHeight = Math.max(baseHeight, Math.min(contentHeight, maxHeight));
    inputRef.current.style.height = `${nextHeight}px`;
    const isOverflowing = contentHeight > maxHeight;
    inputRef.current.style.overflowY = isOverflowing ? "auto" : "hidden";
    inputRef.current.scrollTop = 0;
  }, [inputValue]);

  return (
    <section className="chat-fullscreen">
      <div className="chat-canvas">
        <div className="chat-top-row">
          <button type="button" className="back-button chat-back-button" onClick={onBackHome}>
            <FiArrowLeft className="back-icon" />
            <span>Back to Home</span>
          </button>
        </div>

        <div className="chat-main-content chat-scroll-area" ref={chatScrollRef}>
          <div className="chat-thread">
            {messages.map((message) => {
              if (message.role === "user") {
                return (
                  <div key={message.id} className="chat-user-row">
                    <div className="user-message-card">{message.text}</div>
                    <div className="user-avatar">
                      <FiUser />
                    </div>
                  </div>
                );
              }

              return (
                <div key={message.id} className="chat-assistant-row">
                  <div className="assistant-avatar">
                    <FiCpu />
                  </div>

                  <div className="assistant-content-wrap">
                    {message.isLoading ? (
                      <div className="assistant-loading-block" aria-label="Assistant is thinking">
                        {!message.answer && (!message.reasoning || message.reasoning.length === 0) && (
                          <div className="assistant-loading-state">
                            <span className="assistant-loading-dot" />
                            <span className="assistant-loading-dot" />
                            <span className="assistant-loading-dot" />
                          </div>
                        )}
                        {message.reasoning?.length > 0 && (
                          <details
                            key={`${message.id}-reasoning-${message.answer ? "done" : "live"}`}
                            className={`reasoning-dropdown${!message.answer ? " reasoning-dropdown--live" : ""}`}
                            {...(!message.answer ? { open: true } : {})}
                          >
                            <summary>
                              {!message.answer && <span className="reasoning-pulse" />}
                              <FiChevronDown className="reasoning-chevron" />
                              Reasoning
                            </summary>
                            <div className="reasoning-body">
                              {message.reasoning.map((step, index) => (
                                <div key={`${message.id}-live-reason-${index}`} className="reasoning-step">
                                  <FiCheckCircle className="reasoning-step-icon" />
                                  <span>{step}</span>
                                </div>
                              ))}
                            </div>
                          </details>
                        )}
                        {message.answer && (
                          <div className="assistant-answer-card is-streaming">
                            <div className="assistant-response-markdown">
                              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownLinkRenderer}>{message.answer || ""}</ReactMarkdown>
                            </div>

                            {message.highlights?.length > 0 && (
                              <ul className="assistant-highlights">
                                {message.highlights.map((item, index) => (
                                  <li key={`${message.id}-stream-highlight-${index}`}>{item}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}

                      </div>
                    ) : (
                      <>
                        {message.reasoning?.length > 0 && (
                          <details className="reasoning-dropdown">
                            <summary>
                              <FiChevronDown className="reasoning-chevron" />
                              Reasoning
                            </summary>
                            <div className="reasoning-body">
                              {message.reasoning.map((step, index) => (
                                <div key={`${message.id}-reason-${index}`} className="reasoning-step">
                                  <FiCheckCircle className="reasoning-step-icon" />
                                  <span>{step}</span>
                                </div>
                              ))}
                            </div>
                          </details>
                        )}

                        <div className="assistant-answer-card">
                          <div className="assistant-response-markdown">
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownLinkRenderer}>{message.answer || ""}</ReactMarkdown>
                          </div>

                          {message.highlights?.length > 0 && (
                            <ul className="assistant-highlights">
                              {message.highlights.map((item, index) => (
                                <li key={`${message.id}-highlight-${index}`}>{item}</li>
                              ))}
                            </ul>
                          )}

                          {message.actionsReady && message.actions?.length > 0 && (
                            <div className="assistant-actions">
                              {message.actions.map((action) => (
                                <button
                                  key={`${message.id}-${action.id}`}
                                  type="button"
                                  className="assistant-action-btn"
                                  onClick={() => onActionClick(action.target)}
                                >
                                  {action.label}
                                  <FiArrowRight />
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <form className="chat-bottom-input" onSubmit={handleSubmit}>
          <span className="chat-input-icon-wrap" aria-hidden="true">
            <FiSearch className="chat-input-icon" />
          </span>
          <textarea
            ref={inputRef}
            rows={1}
            value={inputValue}
            onChange={(event) => setInputValue(event.target.value)}
            onKeyDown={handleInputKeyDown}
            className="chat-input-field"
            placeholder="Type your prompt..."
            aria-label="Chat query"
          />
          <button type="button" className="chat-clear-btn" onClick={handleClearChat} aria-label="Clear chat">
            <FaBroom />
          </button>
          <button type="submit" className="chat-send-btn" aria-label="Send query">
            <FiSend />
          </button>
        </form>
      </div>
    </section>
  );
}

export default ChatAssistantWindow;
